import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fetchClerkConfig, loadClerk } from '@/lib/clerk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithClerkToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(searchParams.get('sso') === '1');
  const ssoHandled = useRef(false);

  useEffect(() => {
    fetchClerkConfig().then(cfg => setGoogleEnabled(!!cfg.enabled));
  }, []);

  // Back from Google: swap the Clerk session for a CRM session, then drop the Clerk session.
  useEffect(() => {
    if (searchParams.get('sso_error') === '1') {
      toast.error('Google sign-in was cancelled or failed. Please try again.');
      setSearchParams({}, { replace: true });
      return;
    }
    if (searchParams.get('sso') !== '1' || ssoHandled.current) return;
    ssoHandled.current = true;
    (async () => {
      let clerk;
      try {
        clerk = await loadClerk();
        const clerkToken = await clerk.session?.getToken();
        if (!clerkToken) throw new Error('Google sign-in was cancelled. Please try again.');
        await loginWithClerkToken(clerkToken);
        await clerk.signOut().catch(() => {});
        toast.success('Welcome back!');
        navigate('/dashboard', { replace: true });
      } catch (error) {
        if (clerk) await clerk.signOut().catch(() => {});
        toast.error(error.message || 'Google sign-in failed');
        setSearchParams({}, { replace: true });
        setGoogleBusy(false);
      }
    })();
  }, [searchParams, setSearchParams, loginWithClerkToken, navigate]);

  const handleGoogle = async () => {
    setGoogleBusy(true);
    try {
      const clerk = await loadClerk();
      if (clerk.session) await clerk.signOut();
      await clerk.client.signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/login?sso=1`,
      });
    } catch (error) {
      toast.error(error.message || 'Could not start Google sign-in');
      setGoogleBusy(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
      }}
    >
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8 animate-fade-in">
          <img
            src="/sarum-logo.png"
            alt="Sarum Digital"
            className="w-24 h-24 mx-auto mb-4 rounded-full shadow-[0_0_40px_rgba(244,168,178,0.45)]"
          />
          <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sarum <span className="text-pink-300">Digital</span>
          </h1>
          <p className="text-slate-400 mt-2">Training Centre Dashboard</p>
        </div>

        <Card className="shadow-2xl border-0 bg-slate-800/50 backdrop-blur-lg animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-center text-white">Welcome Back</CardTitle>
            <CardDescription className="text-center text-slate-400">
              Sign in to manage your leads
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10 h-12 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-pink-400 focus:ring-pink-400/20"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-12 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-pink-400 focus:ring-pink-400/20"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-pink-400 to-rose-500 hover:from-pink-500 hover:to-rose-600 text-white font-medium text-base shadow-lg shadow-pink-500/25 transition-all"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            {googleEnabled && (
              <>
                <div className="flex items-center gap-3 my-5">
                  <div className="h-px flex-1 bg-slate-700" />
                  <span className="text-xs text-slate-500">or</span>
                  <div className="h-px flex-1 bg-slate-700" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogle}
                  disabled={googleBusy}
                  className="w-full h-12 bg-white hover:bg-slate-100 text-slate-800 border-0 font-medium text-base"
                  data-testid="login-google-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.3-.4-3.5z"/>
                  </svg>
                  {googleBusy ? 'Connecting to Google…' : 'Continue with Google'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          Powered by Sarum Digital
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
